import { useTheme } from '@mui/material';
import produce, { enableES5 } from 'immer';
import { denormalize, normalize, schema } from 'normalizr';
import { useState } from 'react';
import { MUIDataTableOptions, rowsPerPageOptionsDefault } from 'web-library';
import { CandidateProfileSchema } from '../../../models/@candidate/candidateProfileSchema';
import {
  CandidateResitRegistrationSchema,
  CoordinatorCandidateResitRegistration,
} from '../../../models/@candidate/candidateResitRegistrationSchema';
import { fallbackEmptyObject } from '../../../utils/ComponentUtils';

enableES5();

/**
 * A hook to provide candidate listing table options
 * @param selectedCandidate Row selected (single) with row click, not checkbox
 * @param setSelectedCandidate Function to change selected candidate
 * @param rowsSelected Row selected indexes with checkbox, not row click
 * @param setRowsSelected  Function to change selected rows
 * @param dataLength Data length the table will get
 * @param rowOptions rowOptions for paginations
 * @param tableColumns table column definition
 * @returns
 */
export const useCandidateTable = (dataLength: number, rowOptions: number[]) => {
  const theme = useTheme();

  const [selectedCandidate, setSelectedCandidate] = useState<number | null>(null);
  const [rowsSelected, setRowsSelected] = useState<any[]>([]);

  const options: MUIDataTableOptions = {
    fixedHeader: false,
    rowsPerPageOptions: rowsPerPageOptionsDefault,
    selectableRowsOnClick: false,
    selectableRows: 'multiple',
    setRowProps: (_row: string[], _dataIndex: number, rowIndex: number) => {
      if (selectedCandidate === _dataIndex) {
        return {
          style: { backgroundColor: theme.color.background.bgMain },
        };
      }
      return {
        style: { backgroundColor: 'transparent' },
      };
    },
  };

  return {
    selectedCandidate,
    setSelectedCandidate,
    rowsSelected,
    setRowsSelected,
    options,
    tableColumns: [],
  };
};

const subject = new schema.Entity(
  'subjects',
  {},
  {
    idAttribute: (entity, parent) => {
      const preSelectPostfix = entity.preSelected ? 'pre' : 'notPre';
      return `${parent.nricFin}-${entity.subjectCode}::${preSelectPostfix}`;
    },
  }
);
const resitCandidate = new schema.Entity(
  'resitCandidates',
  {
    subjects: [subject],
  },
  { idAttribute: 'nricFin' }
);
const resitCandidateList = new schema.Array(resitCandidate);

export const denormalizedCandidate = (state: any) => {
  const entities = {
    resitCandidates: { ...state.resitCandidates },
    subjects: { ...state.subjects },
  };
  const denormlizeData = denormalize(state.candidateID, resitCandidateList, entities);

  // apply only selected subjects
  return produce(denormlizeData, (draft: any[]) => {
    draft.forEach((candidateObj, idx) => {
      if (candidateObj.selectedSubjects.length > 0) {
        draft[idx].subjects = candidateObj.selectedSubjects.map((num: number) => ({
          ...candidateObj.subjects[num],
        }));
        delete draft[idx].selectedSubjects;
      } else {
        draft.splice(idx, 1);
      }
    });
  });
};

interface CoordinatorResitStates {
  candidateID: string[];
  resitCandidates: { [key: string]: any } | undefined;
  subjects: { [key: string]: any } | undefined;
  internalTransactionRef: string | null;
  noOfCandidate: number; // with selected subject
}

export const INITIAL_COORDINATOR_RESIT = {
  candidateID: [],
  resitCandidates: {},
  subjects: {},
  internalTransactionRef: null,
  noOfCandidate: 0,
};

export type CoordinatorResitAction =
  | { type: 'INIT_LIST'; payload: CoordinatorCandidateResitRegistration }
  | { type: 'ADD_CANDIDATE'; payload: CandidateResitRegistrationSchema }
  | { type: 'DELETE_CANDIDATES'; payload: string[] }
  | { type: 'UPDATE_SUBJECTS_SELECTION'; payload: { selection: number[]; candidateId: string } }
  | {
      type: 'UPDATE_CANDIDATE_PARTICULARS';
      payload: { candidateParticular: CandidateProfileSchema; candidateId: string };
    }
  | {
      type: 'UPDATE_INTERNAL_TRANSACTION_REF';
      payload: string;
    }
  | {
      type: 'CLEAR_INTERNAL_TRANSACTION_REF';
    }
  | {
      type: 'UPDATE_NO_OF_CANDIDATE';
      payload: number;
    };

const preSelectReduceFunction = (accumulator: any, currentValue: any, currentIndex: number) => {
  const splitId = currentValue.split('::');
  if (splitId.length === 2 && splitId[1] === 'pre') {
    accumulator.push(currentIndex);
  }
  return accumulator;
};

// object in JS pass by reference
const initList = (
  draft: CoordinatorResitStates,
  action: { type: 'INIT_LIST'; payload: CoordinatorCandidateResitRegistration }
) => {
  const norm = normalize(action.payload.candidateDetails, resitCandidateList);
  draft.candidateID = norm.result;
  draft.resitCandidates = norm.entities.resitCandidates;
  // duplicate selected subjects from subjects, preselect

  draft.candidateID.forEach((key) => {
    if (draft.resitCandidates?.[key]) {
      draft.resitCandidates[key].selectedSubjects = draft.resitCandidates[key].subjects.reduce(
        preSelectReduceFunction,
        []
      );
    }
  });

  draft.subjects = { ...norm.entities.subjects };
  Object.keys(draft.subjects).forEach((key) => {
    if (draft.subjects?.[key]) {
      draft.subjects[key] = {
        ...draft.subjects[key],
        examFees: null,
        gst: null,
      };
    }
  });
};

export function coordinatorResitReducer(
  draft: CoordinatorResitStates,
  action: CoordinatorResitAction
) {
  switch (action.type) {
    case 'INIT_LIST': {
      initList(draft, action);
      break;
    }
    case 'ADD_CANDIDATE': {
      const norm = normalize(action.payload, resitCandidate);
      if (!draft.candidateID.includes(norm.result)) {
        draft.candidateID.push(norm.result);
        draft.resitCandidates = { ...draft.resitCandidates, ...norm.entities.resitCandidates };
        draft.subjects = { ...draft.subjects, ...norm.entities.subjects };
      }
      break;
    }
    case 'DELETE_CANDIDATES': {
      action.payload.forEach((nric) => {
        const candidateIdx = draft.candidateID.indexOf(nric);
        draft.candidateID.splice(candidateIdx, 1);

        const tmpCandidate = fallbackEmptyObject(draft.resitCandidates?.[nric].subjects);

        tmpCandidate.forEach((sub: string) => {
          if (draft.subjects?.[sub]) {
            delete draft.subjects[sub];
          }
        });

        if (draft.resitCandidates?.[nric]) {
          delete draft.resitCandidates[nric];
        }
      });
      break;
    }
    case 'UPDATE_SUBJECTS_SELECTION': {
      if (draft.resitCandidates?.[action.payload.candidateId]) {
        draft.resitCandidates[action.payload.candidateId].selectedSubjects =
          action.payload.selection;
      }
      break;
    }
    case 'UPDATE_CANDIDATE_PARTICULARS': {
      if (draft.resitCandidates?.[action.payload.candidateId]) {
        draft.resitCandidates[action.payload.candidateParticular.nricFin] = {
          ...action.payload.candidateParticular,
        };
      }
      break;
    }
    case 'UPDATE_INTERNAL_TRANSACTION_REF': {
      draft.internalTransactionRef = action.payload;
      break;
    }
    case 'CLEAR_INTERNAL_TRANSACTION_REF': {
      draft.internalTransactionRef = null;
      break;
    }
    case 'UPDATE_NO_OF_CANDIDATE': {
      draft.noOfCandidate = action.payload;
      break;
    }
    default:
      throw new Error();
  }
}
